/**
 * ReactGantt chart and the parked look-ahead quantity panel for /schedule/tasks.
 * Route: /schedule/tasks.
 * Does not own task data, column templates, or dialog state.
 */
import { Fragment } from 'react';
import { GanttTargetsPanel } from '../components/targets';
import { ENABLE_TARGETS_QUANTITY_PANEL } from '../ganttPageFlags';
import ReactGantt from '@dhx/react-gantt';

export function GanttChartPane(p: Record<string, any>) {
  const {
    viewMode,
    ganttRef,
    ganttSkinTheme,
    calendarReady,
    tasks,
    links,
    compareBaselinesEnabled,
    dhtmlxBaselines,
    config,
    plugins,
    todayMarkers,
    templates,
    ganttTaskFilter,
    handleBeforeTaskDrag,
    handleAfterTaskDrag,
    handleTaskUpdate,
    handleLinkAdd,
    handleLinkDelete,
    handleBeforeAutoSchedule,
    handleAutoSchedule,
    handleTaskSelected,
    targetsVisibleRows,
    lookAheadLeafTaskIds,
    GANTT_ROW_HEIGHT,
    targetsColumns,
    canMutateTasks,
    targetsData,
    store,
  } = p;

  return (
    <Fragment>
  {/* Gantt chart - grows to fill available space; quantity panel is parked behind flag */}
  <div
    style={{
      flex: 1,
      overflow: 'hidden',
      minHeight: '300px',
      display: 'flex',
      flexDirection: 'row',
    }}
  >
    <div
      className={
        ENABLE_TARGETS_QUANTITY_PANEL && viewMode === 'targets'
          ? 'fbt-gantt-targets-mode'
          : undefined
      }
      style={{
        flex:
          ENABLE_TARGETS_QUANTITY_PANEL && viewMode === 'targets'
            ? '0 0 46%'
            : 1,
        minWidth:
          ENABLE_TARGETS_QUANTITY_PANEL && viewMode === 'targets' ? 360 : 0,
        maxWidth:
          ENABLE_TARGETS_QUANTITY_PANEL && viewMode === 'targets'
            ? '60%'
            : undefined,
        overflow: 'hidden',
        height: '100%',
        position: 'relative',
        borderRight:
          ENABLE_TARGETS_QUANTITY_PANEL && viewMode === 'targets'
            ? '1px solid var(--gantt-cell-border, var(--mantine-color-default-border))'
            : undefined,
      }}
    >
      <ReactGantt
        ref={ganttRef}
        theme={ganttSkinTheme}
        tasks={calendarReady ? tasks : []}
        links={calendarReady ? links : []}
        baselines={
          calendarReady && compareBaselinesEnabled && viewMode === 'schedule'
            ? dhtmlxBaselines
            : []
        }
        config={config}
        plugins={plugins}
        markers={todayMarkers}
        templates={templates}
        // Pro 10.x defaults to basic-sanitize, which strips <button>/<select>
        // from grid column templates (edit / add / delete / task-type controls).
        // Cell content is already escapeHtml'd where user-controlled.
        htmlTemplatePolicy="unsafe-html"
        filter={ganttTaskFilter}
        onBeforeTaskDrag={handleBeforeTaskDrag}
        onAfterTaskDrag={handleAfterTaskDrag}
        onAfterTaskUpdate={handleTaskUpdate}
        onAfterLinkAdd={handleLinkAdd}
        onAfterLinkDelete={handleLinkDelete}
        onBeforeAutoSchedule={handleBeforeAutoSchedule}
        onAfterAutoSchedule={handleAutoSchedule}
        onTaskSelected={handleTaskSelected}
      />
    </div>
    {/*
      Quantity P|A panel — final look-ahead targets editing surface.
      Parked via ENABLE_TARGETS_QUANTITY_PANEL; keep wiring intact for later.
    */}
    {ENABLE_TARGETS_QUANTITY_PANEL && viewMode === 'targets' ? (
      <GanttTargetsPanel
        ganttRef={ganttRef}
        visibleRows={targetsVisibleRows}
        getTask={(id) => store.getTask(id)}
        lookAheadLeafTaskIds={lookAheadLeafTaskIds ?? new Set()}
        rowHeight={GANTT_ROW_HEIGHT}
        columns={targetsColumns}
        canEdit={canMutateTasks}
        getCellValue={targetsData.getCellValue}
        setCellTarget={targetsData.setCellTarget}
        setCellTargets={targetsData.setCellTargets}
        isCellDirty={targetsData.isCellDirty}
      />
    ) : null}
  </div>
    </Fragment>
  );
}
